import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Clock, MapPin, Bus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { buildWhatsAppLink, tripEnquiryMessage } from "../lib/whatsapp";
import { Reveal } from "../components/site/Reveal";
import { WhatsAppIcon } from "../components/site/Icons";
import Footer from "../components/site/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const BOARDING_POINTS = ["Hyderabad", "Vijayawada", "Guntur", "Kurnool", "Anantapur", "Tirupati", "Warangal", "Other"];

export default function TripDetail() {
  const { slug } = useParams();
  const { settings } = useSettings();
  const [trip, setTrip] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", boarding_point: "", travellers: 1, preferred_date: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/trips/${slug}`).then((r) => setTrip(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6">
      <p className="font-heading text-4xl">Journey not found</p>
      <Link to="/" className="text-saffron underline" data-testid="back-home-link">Back home</Link>
    </div>
  );
  if (!trip) return <div className="min-h-screen flex items-center justify-center text-muted2">Loading…</div>;

  const wa = buildWhatsAppLink(settings?.whatsapp_number, tripEnquiryMessage(settings?.agency_name, trip));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please enter your name and phone."); return; }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { ...form, travellers: Number(form.travellers), trip_id: trip.id, trip_title: trip.title });
      toast.success("Enquiry sent! Redirecting you to WhatsApp…");
      const msg = `${tripEnquiryMessage(settings?.agency_name, trip)}\n\nName: ${form.name}\nTravellers: ${form.travellers}${form.boarding_point ? `\nBoarding: ${form.boarding_point}` : ""}${form.preferred_date ? `\nPreferred date: ${form.preferred_date}` : ""}${form.message ? `\nNote: ${form.message}` : ""}`;
      window.open(buildWhatsAppLink(settings?.whatsapp_number, msg), "_blank");
      setForm({ name: "", phone: "", boarding_point: "", travellers: 1, preferred_date: "", message: "" });
    } catch {
      toast.error("Could not send enquiry. Please try WhatsApp directly.");
    } finally { setSubmitting(false); }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[70vh] min-h-[520px] overflow-hidden" data-testid="trip-hero">
        <img src={trip.hero_image} alt={trip.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-obsidian/20 to-obsidian/80" />
        <div className="relative h-full max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-end pb-16">
          <Link to="/" className="absolute top-28 left-5 sm:left-8 inline-flex items-center gap-2 text-bone/80 hover:text-bone text-sm" data-testid="trip-back-link">
            <ArrowLeft className="w-4 h-4" /> All journeys
          </Link>
          <div className="flex items-center gap-4 mb-5 text-bone/85 text-sm">
            <span className="inline-flex items-center gap-1.5 bg-bone/15 backdrop-blur px-3 py-1.5 rounded-full"><Clock className="w-4 h-4 text-saffron" />{trip.duration === "2-day" ? "2 Days" : "1 Day"}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-saffron" />{trip.location}</span>
          </div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="font-heading text-bone text-5xl sm:text-7xl font-light tracking-tighter leading-none max-w-4xl">{trip.title}</motion.h1>
          <p className="text-bone/75 mt-5 text-lg max-w-2xl">{trip.subtitle}</p>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-3 gap-16">
        {/* Left content */}
        <div className="lg:col-span-2 space-y-16">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-saffron mb-4">The journey</p>
            <p className="text-xl sm:text-2xl leading-relaxed text-obsidian/90 font-light">{trip.overview}</p>
          </Reveal>

          {trip.highlights?.length > 0 && (
            <Reveal>
              <div className="flex flex-wrap gap-3">
                {trip.highlights.map((h, i) => (
                  <span key={i} className="border border-border bg-sand/50 rounded-full px-4 py-2 text-sm text-obsidian/80">{h}</span>
                ))}
              </div>
            </Reveal>
          )}

          {trip.itinerary?.length > 0 && (
            <Reveal>
              <h2 className="font-heading text-4xl mb-10">Itinerary</h2>
              <div className="space-y-0">
                {trip.itinerary.map((it, i) => (
                  <div key={i} className="grid grid-cols-[110px_1fr] sm:grid-cols-[160px_1fr] gap-5 border-t border-border py-7 group">
                    <div className="text-saffron font-mono text-sm pt-1">{it.time}</div>
                    <div>
                      <h4 className="font-heading text-2xl">{it.title}</h4>
                      {it.description && <p className="text-muted2 mt-2 leading-relaxed">{it.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          <Reveal>
            <div className="grid sm:grid-cols-2 gap-10">
              <div>
                <h3 className="font-heading text-2xl mb-5">What&apos;s included</h3>
                <ul className="space-y-3">
                  {trip.inclusions?.map((x, i) => (
                    <li key={i} className="flex items-start gap-3 text-obsidian/80"><Check className="w-5 h-5 text-saffron shrink-0 mt-0.5" />{x}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading text-2xl mb-5">Not included</h3>
                <ul className="space-y-3">
                  {trip.exclusions?.map((x, i) => (
                    <li key={i} className="flex items-start gap-3 text-muted2"><X className="w-5 h-5 text-muted2 shrink-0 mt-0.5" />{x}</li>
                  ))}
                </ul>
              </div>
            </div>
          </Reveal>
        </div>

        {/* Booking card */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-28 border border-border rounded-md bg-white p-7" data-testid="booking-card">
            <div className="flex items-end justify-between border-b border-border pb-6 mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-saffron">Personalised pricing</p>
                <p className="font-heading text-3xl mt-2 leading-tight">Speak to us on WhatsApp</p>
                <p className="text-muted2 text-sm mt-2">We tailor the fare to your group size, boarding point and dates.</p>
                {trip.note && <p className="text-muted2 text-xs italic mt-3 max-w-[260px]">{trip.note}</p>}
              </div>
              {trip.departure && <span className="inline-flex items-center gap-1.5 text-sm text-obsidian/70 whitespace-nowrap ml-3"><Bus className="w-4 h-4 text-saffron" />{trip.departure}</span>}
            </div>

            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-saffron hover:bg-saffron-hover text-bone py-3.5 rounded-full transition-colors mb-6">
              <WhatsAppIcon className="w-5 h-5" /> Book instantly on WhatsApp
            </a>

            <p className="text-center text-muted2 text-sm mb-5">or send an enquiry</p>

            <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="enquiry-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" data-testid="enquiry-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="boarding">Boarding city</Label>
                <Select value={form.boarding_point} onValueChange={(v) => setForm({ ...form, boarding_point: v })}>
                  <SelectTrigger id="boarding" data-testid="enquiry-boarding" className="mt-1.5"><SelectValue placeholder="Choose your pickup city" /></SelectTrigger>
                  <SelectContent>
                    {BOARDING_POINTS.map((p) => <SelectItem key={p} value={p} data-testid={`boarding-option-${p.toLowerCase()}`}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="travellers">Travellers</Label>
                  <Input id="travellers" type="number" min="1" data-testid="enquiry-travellers" value={form.travellers} onChange={(e) => setForm({ ...form, travellers: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input id="date" type="date" data-testid="enquiry-date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label htmlFor="msg">Message</Label>
                <Textarea id="msg" data-testid="enquiry-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Any special request…" className="mt-1.5" rows={3} />
              </div>
              <Button type="submit" disabled={submitting} data-testid="enquiry-submit"
                className="w-full bg-obsidian hover:bg-obsidian/90 text-bone rounded-full py-6">
                {submitting ? "Sending…" : "Send enquiry"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
